/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Table, Input, Button, Row, Col, Card, Badge, Select, message } from "antd";
import { SearchOutlined, RetweetOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { LockingService } from "../../../services/api/locking";
import "./pendingAwbs.scss";

const { Option } = Select;

const PendingAwbs = () => {
    const [searchParams] = useSearchParams();
    const cpId = searchParams.get("cp_id");
    const cpName = searchParams.get("cp_name");

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState([]);
    const [ruleFilter, setRuleFilter] = useState([]);

    const fetchData = async () => {
        if (!cpId) return;
        setLoading(true);
        try {
            const response = await LockingService.getHubPendingAwbs(cpId);
            // Handle nested response: { status: "success", data: { pending_awbs: [...], total_count: 1 } }
            const resultData = response?.data?.pending_awbs || response?.data || response || [];

            const mappedData = Array.isArray(resultData) ? resultData.map((item, index) => ({
                key: item.awb_number || item.awbNumber || `awb-${index}`,
                sNo: index + 1,
                awbNumber: item.awb_number || item.awbNumber || "---",
                lockRule: item.rule_id || item.ruleId || "---",
                hubAtEvent: item.hub_at_event || item.hubAtEvent || "---",
                receivedAt: item.received_at || item.receivedAt || "---",
                status: item.status || "---"
            })) : [];

            setData(mappedData);
            setFilteredData(mappedData);
            setTotalCount(response?.data?.total_count || response?.total_count || response?.total_pending_awbs || mappedData.length);
        } catch (error) {
            console.error("Error fetching pending AWBs:", error);
            message.error("Failed to fetch pending AWBs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [cpId]);

    // Local filtering logic
    useEffect(() => {
        let result = [...data];

        if (searchText) {
            const lowerSearch = searchText.toLowerCase();
            result = result.filter(item =>
                item.awbNumber.toLowerCase().includes(lowerSearch) ||
                item.hubAtEvent.toLowerCase().includes(lowerSearch)
            );
        }

        if (statusFilter && statusFilter.length > 0) {
            result = result.filter(item =>
                statusFilter.includes(item.status)
            );
        }

        if (ruleFilter && ruleFilter.length > 0) {
            result = result.filter(item => ruleFilter.includes(item.lockRule));
        }

        setFilteredData(result);
    }, [searchText, statusFilter, ruleFilter, data]);

    const uniqueRules = [...new Set(data.map(item => item.lockRule))].filter(r => r !== "---");

    const columns = [
        {
            title: "S.No",
            dataIndex: "sNo",
            key: "sNo",
            width: 80,
            align: "center",
        },
        {
            title: "Reference Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            align: "center",
            render: (text) => <strong>{text}</strong>
        },
        {
            title: "Lock Rule",
            dataIndex: "lockRule",
            key: "lockRule",
            align: "center",
        },
        {
            title: "Received At",
            dataIndex: "receivedAt",
            key: "receivedAt",
            align: "center",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (status) => (
                <Badge
                    count={status}
                    style={{
                        backgroundColor: "#ff4d4f",
                        borderRadius: "2px",
                        padding: "0 8px",
                        height: "22px",
                        lineHeight: "22px",
                        fontSize: "11px"
                    }}
                />
            ),
        },
    ];

    return (
        <div className="pending-awbs-page">
            <Card className="summary-gradient-card">
                <Row gutter={16}>
                    <Col>
                        <Card className="info-badge">
                            HUB: {cpName || "---"} ({cpId || "---"})
                        </Card>
                    </Col>
                    <Col>
                        <Card className="info-badge">
                            Total Pending: {totalCount}
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Card className="table-wrapper-card">
                <Row gutter={16} className="filter-row" align="middle">
                    <Col flex="1">
                        <Input
                            placeholder="Search by Reference Number"
                            prefix={<SearchOutlined />}
                            className="filter-item"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>
                    <Col flex="1">
                        <Select
                            mode="multiple"
                            placeholder="Filter by Status"
                            className="filter-item"
                            style={{ width: "100%" }}
                            maxTagCount="responsive"
                            allowClear
                            value={statusFilter}
                            onChange={(val) => setStatusFilter(val)}
                        >
                            <Option value="LOCKED">LOCKED</Option>
                            <Option value="OPEN">OPEN</Option>
                        </Select>
                    </Col>
                    <Col flex="1">
                        <Select
                            mode="multiple"
                            placeholder="Filter by Lock Rule"
                            className="filter-item"
                            style={{ width: "100%" }}
                            maxTagCount="responsive"
                            allowClear
                            value={ruleFilter}
                            onChange={(val) => setRuleFilter(val)}
                        >
                            {uniqueRules.map(rule => (
                                <Option key={rule} value={rule}>{rule}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<RetweetOutlined />}
                            className="refresh-btn"
                            onClick={fetchData}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        pageSizeOptions: ["15", "30", "50"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} total`,
                    }}
                    className="awb-table"
                />
            </Card>
        </div>
    );
};

export default PendingAwbs;
